import { useTranslation } from 'react-i18next';
import { Info, UserCog, Plus } from 'lucide-react';

import { ActionButton } from '@/shared/ui/action-button';
import { SimpleSelect } from '@/shared/ui/filters';
import { FormField } from '@/shared/ui';
import type { SelectOption } from '@/shared/ui';
import { cn } from '@/lib/utils';

interface ContactSelectProps {
  label: string;
  contactId?: string;
  options: SelectOption[];
  isLoading: boolean;
  disabled?: boolean;
  showDetails?: boolean;
  required?: boolean;
  error?: string;
  /** Query the field starts with, e.g. the contact name an SRF import matched but never saved. */
  initialQuery?: string;
  /** A contact belongs to an organization: none chosen, nothing to add it to. */
  canAdd?: boolean;
  onChange: (id: string | undefined) => void;
  onDetailsClick: () => void;
  onEditClick: () => void;
  onAddClick: () => void;
}

export const ContactSelect = ({
  label,
  contactId,
  options,
  isLoading,
  disabled = false,
  showDetails = false,
  required = false,
  error,
  initialQuery,
  canAdd = true,
  onChange,
  onDetailsClick,
  onEditClick,
  onAddClick
}: ContactSelectProps) => {
  const { t } = useTranslation();

  return (
    <FormField label={label} name="contact" required={required} error={error}>
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">
          <SimpleSelect
            options={options}
            value={contactId}
            onChange={onChange}
            isLoading={isLoading}
            placeholder={t('common.selectContact')}
            initialQuery={initialQuery}
            className="w-full"
            disabled={disabled}
          />
        </div>
        <div className="flex gap-1 shrink-0">
          {contactId && (
            <>
              <ActionButton
                type="button"
                intent="view"
                size="icon"
                onClick={onDetailsClick}
                className={cn('w-11', showDetails && 'bg-green-100 border-green-400 text-green-700')}
                title={t('gen.details.label')}>
                <Info className="w-4 h-4" />
              </ActionButton>
              <ActionButton
                type="button"
                intent={disabled ? 'view' : 'action'}
                size="icon"
                onClick={onEditClick}
                className="w-11"
                title={disabled ? t('contacts.viewContact') : t('contacts.editContact')}>
                <UserCog className="w-4 h-4" />
              </ActionButton>
            </>
          )}
          {!disabled && (
            <ActionButton
              type="button"
              intent="action"
              size="icon"
              onClick={onAddClick}
              disabled={!canAdd}
              className="w-11"
              title={t('contacts.addContact')}>
              <Plus className="w-4 h-4" />
            </ActionButton>
          )}
        </div>
      </div>
    </FormField>
  );
};
